import {Component} from "./Component";
import {filterEventSelector} from "../util/event";
import {createLogger} from "../util/logger";
import {delayPromise} from "../util/promise";
import {fetchPage} from "../util/ajax";
import {PageLoader} from "./Navigation/PageLoader.js";
import {indexBy} from "../util/array";
import {prefetch} from "../util/prefetch";

const console = createLogger("AjaxNavigation");

/**
 * AjaxNavigation Class
 *
 * Manages AJAX navigation on a website by listening to all clicks on <a> elements
 * and handling fetching and rendering of news pages.
 *
 * When a new page was fetched it is rendered into data-ajax-navigation="relaceTarget"
 * Excluded links are:
 * - [download] attribute
 * - [target=_blank]
 * - [class=native-link]
 * - /wp-admin/admin-post.php
 * - hash links
 *
 *
 * @extends Component
 */
export class AjaxNavigation extends Component {

    static name = "ajax-navigation";

    /**
     * Event triggered when the AJAX navigation begins.
     * @type {string}
     */
    static EVENT_LOAD_START = "ajax-navigation:load-start";

    /**
     * Event triggered when the AJAX navigation ends.
     * @type {string}
     */
    static EVENT_LOAD_END = "ajax-navigation:load-end";

    /**
     * Singleton instance
     * @type {AjaxNavigation|null}
     */
    static instance = null;


    get name() {
        return AjaxNavigation.name;
    }

    constructor(el) {
        super(el);

        this.enabled = true;

        AjaxNavigation.instance = this;

        this.currentUrl = window.location.href.replace(/\/$/, "");

        this.pendingClassName = this.getClassName("--pending");

        this.$menuItems = this.querySelectorAll(".menu-item");

        this.$replaceTarget = this.$("replaceTarget");

        this.addEventListener(this.el, "click", filterEventSelector("a, [data-href]", this.onClickLink.bind(this)));

        this.addEventListener(window, "popstate",this.onWindowPopState.bind(this));

        this.onMouseEnter = this.onMouseEnter.bind(this);
        this.initPrefetch();
    }

    /**
     * Initialize prefetching by adding mouse enter event listener to <a> elements.
     */
    initPrefetch() {
        const a = this.querySelectorAll("a");
        this.removeEventListener(a, "mouseenter");

        this.addEventListener(a, "mouseenter", this.onMouseEnter.bind(this));
    }

    /**
     * Handle popstate event
     *
     * Handles popstate event and navigates to page if possible
     *
     * @param {PopStateEvent} e - The popstate event.
     */
    onWindowPopState(e) {

        if (!this.enabled) return;

        const current = new URL(this.currentUrl);
        current.hash = "";

        // If only hash changed return
        if (window.location.href.startsWith(current.toString()) && window.location.hash) {
            return;
        }
        console.log("onWindowPopState", {
            prev: this.currentUrl,
            next: window.location.href,
            startsWithh: window.location.href.startsWith(this.currentUrl)
        });
        this.fetchUrl(window.location.href);
    }

    /**
     * mouseenter listener which prefetches pages if applicable
     *
     * @param {MouseEvent} e
     */
    onMouseEnter(e) {
        const a = e.target.closest("a");

        console.log("onMouseEnter", a, this.isValidLink(a));

        if (!this.isValidLink(a)) return;

        const url = new URL(a.getAttribute("href"));
        url.searchParams.set("ajax", "1");

        prefetch(url.toString());
    }

    /**
     * click listener on links which triggers ajax page load if link is valid
     *
     * @param {MouseEvent} e
     */
    onClickLink(e) {
        const a = e.target.closest("a, [data-href]");
        const href = a.getAttribute("href") || a.getAttribute("data-href");

        if (!this.enabled) return;

        if (!this.isValidLink(a)) return;

        e.preventDefault();

        if (href === this.currentUrl) return;

        this.fetchUrl(href);
    }

    /**
     * Checks link element if it is valid link for ajax navigation
     *
     * @param {HTMLAnchorElement} a
     * @return {boolean}
     */
    isValidLink(a) {
        const href = a.getAttribute("href");

        return !(!href?.startsWith(window.location.origin)
            // Exclude hash links
            || href.startsWith("#")
            // Exclude wordpress admin links
            || href.includes("/wp-admin/admin-post.php")
            // Exclude links opening in new tab
            || a.getAttribute("target") === "_blank"
            // Exclude links with class native-link
            || a.classList.contains("native-link")
            // Exclude download links
            || a.hasAttribute("download"));
    }

    /**
     * Fetches HTML from {url} and renders the result on the page
     *
     * @param url
     * @param {bool} replaceHistory
     * @return {Promise<unknown>}
     */
    async fetchUrl(url, replaceHistory = false) {
        if (!this.enabled) return;

        // this.el.classList.add(this.pendingClassName);
        await PageLoader.show(this.el);

        this.currentUrl = url;

        this.dispatchDomEvent(AjaxNavigation.EVENT_LOAD_START, {url});

        return delayPromise(0, fetchPage(url)).then((html) => {
            const sourceBody = html.querySelector("body");
            const sourceClasses = sourceBody.getAttribute("data-class").split(" ");

            const removeClasses = document.body.getAttribute("data-class").split(" ").filter(c => !sourceClasses.includes(c));

            console.log("replaceClasses", {bodyClasses: document.body.classList.toString(), sourceClasses, removeClasses});

            document.body.classList.remove(...removeClasses);
            // Remove theme classes
            document.body.className = document.body.className.replace(/\btheme-\w+\b/g, "");

            document.body.classList.add(...sourceClasses);
            document.body.setAttribute("data-class", sourceClasses.join(" "));

            // document.body.classList.value = html.querySelector("body").classList.value;

            window.scrollTo({
                top: 0,
            });

            const $source = html.querySelector(this.getAttributeSelector("replaceTarget"));

            this.$replaceTarget.replaceWith($source);
            this.$replaceTarget = $source;

            window.history[replaceHistory ? "replaceState" : "pushState"]({}, null, url)

            this.setActiveMenuItem(url);

            this.replaceElements(html);

            this.dispatchDomEvent(AjaxNavigation.EVENT_LOAD_END, {url, target: this.$replaceTarget});

            this.updateScriptTags(html);
            this.updateMetaTags(html);

            this.initPrefetch();

            return PageLoader.hide(this.el);
            // this.el.classList.remove(this.pendingClassName);
        });
    }

    /**
     * Adds .current-menu-item to .menu-item when href matches current url
     * @param href
     */
    setActiveMenuItem(href) {
        this.$menuItems.forEach((item) => {
            if (item.firstElementChild.getAttribute("href") === href) {
                item.classList.add("current-menu-item");
            } else {
                item.classList.remove("current-menu-item");
            }
        });
    }


    updateMetaTags(html) {
        document.title = html.querySelector("title").innerText;

        const description = document.querySelector("meta[name=\"description\"]");
        if (description) {
            description.innerText = html.querySelector("meta[name=\"description\"]")?.innerText;
        }
    }

    /**
     * Loads new scripts in {html}
     *
     * @param html
     */
    updateScriptTags(html) {
        const _indexFn = (el) => {
            if (el.id) return el.id;

            return el.getAttribute("src");
        };

        // Get new document script tags
        const newTags = indexBy(Array.from(html.querySelectorAll("script")), _indexFn);

        // Get current document script tags
        const currentTags = indexBy(Array.from(document.querySelectorAll("script")), _indexFn);

        Object.keys(newTags).forEach((key) => {
            if (currentTags[key]) return;

            const inHead = newTags[key].parentElement.tagName === "HEAD";

            const src = newTags[key].getAttribute("src");
            const script = document.createElement("script");
            script.id = newTags[key].id;
            script.async = true;

            if (src) {
                script.src = src;
            } else {
                script.innerHTML = newTags[key].innerHTML;
            }


            console.log("updateScriptTag", {key, el: newTags[key], inHead});
            const first = document[inHead ? "head" : "body"].getElementsByTagName("script")[0];
            first.parentNode.insertBefore(script, first);
        });
    }

    /**
     * Replaces elemenets with attribute data-ajax-navigation-replace
     * @param {HTMLElement} html
     */
    replaceElements(html) {

        const attr = this.getComponentAttributeName("replace");
        const selector = `[${attr}]`;

        const replaceSource = Array.from(html.querySelectorAll(selector));

        console.log("replaceElements", replaceSource);

        replaceSource.forEach(el => {
            const name = el.getAttribute(attr);
            const replaceSelector = `[${attr}="${name}"]`;

            const replaceEl = this.querySelector(replaceSelector);

            console.log("replaceElement", {name, replaceSelector, replaceEl});

            if (replaceEl) {
                replaceEl.replaceWith(el);
            }
        });
    }

    /**
     * Helper to navigate to different page
     * @param {string} url
     * @param {boolean} replace
     * @return {Promise<any>}
     */
    static navigate(url, replace = false) {
        return AjaxNavigation.instance.fetchUrl(url, replace);
    }

    disable() {
        console.log("disable", this.enabled);
        this.enabled = false;
    }

    enable() {
        console.log("enable", this.enabled);
        this.enabled = true;
    }

}
